<script setup lang="ts">
import type { DropdownMenuItem } from '#ui/types';
import type { PropType } from 'vue';

import { type PaymentMethodEntity } from '~/apiClient';
import { isCardPaymentMethod } from '~/types/paymentMethod.type';

// PROPS
const { paymentMethod } = defineProps({
    paymentMethod: { type: Object as PropType<PaymentMethodEntity>, required: true },
});

// COMPOSABLES
const { tt } = useTypedI18n();
const { paymentMethodLabel, isExpired, expirationLabel } = usePaymentMethodInfo(() => paymentMethod);

// DATA
const isUpdateExpirationModalOpen = ref(false);
const isDeleteModalOpen = ref(false);

const menuItems: DropdownMenuItem[][] = [
    [
        {
            label: tt('paymentMethods.updateExpiration'),
            onSelect: (): void => {
                isUpdateExpirationModalOpen.value = true;
            },
        },
        {
            label: tt('common.delete'),
            color: 'error',
            onSelect: (): void => {
                isDeleteModalOpen.value = true;
            },
        },
    ],
];
</script>

<template>
    <!-- N.B. For now, it only supports the card payment method -->
    <template v-if="isCardPaymentMethod(paymentMethod)">
        <div :class="'flex flex-col gap-y-4 rounded-xl border border-neutral-300 p-6'">
            <div class="flex flex-row items-start justify-between">
                <UIcon :name="`pmt:card-${paymentMethod.card.brand}`" class="h-8 w-12" />
                <UDropdownMenu
                    :items="menuItems"
                    :content="{
                        align: 'end',
                        side: 'bottom',
                    }"
                >
                    <UButton icon="lucide:ellipsis-vertical" size="md" color="neutral" variant="ghost" />
                </UDropdownMenu>
            </div>
            <p class="font-bold uppercase">{{ paymentMethodLabel }}</p>
            <p :class="isExpired ? 'text-error-500' : 'text-muted'">{{ expirationLabel }}</p>
        </div>

        <PaymentMethodsUpdateExpirationModal v-model:open="isUpdateExpirationModalOpen" :payment-method="paymentMethod" />
        <PaymentMethodsDeleteModal v-model:open="isDeleteModalOpen" :payment-method="paymentMethod" />
    </template>
</template>
